<template>
    <div ref="root" :class="stickyClassName" :style="stickyStyle">
        <slot />
    </div>
</template>

<script lang="ts">
import debounce from 'lodash/debounce';
import { mapState } from 'pinia';
import { TopMarginResult, useNavigationStore } from '../../stores/navigation';

export default {
    props: {
        enabled: { type: Boolean, default: true },
        minHeightRatio: { type: Number, default: 0.5 },
        stickyClass: { type: String, default: 'xl:sticky' },
    },
    data() {
        return {
            root: null as HTMLElement | null,
            stickyEnabled: false,
            resizeHandler: null as ((() => void) & { cancel: () => void }) | null,
        };
    },
    computed: {
        ...mapState(useNavigationStore, ['additionalTopMarginContent']),
        topMarginContent(): TopMarginResult {
            // Keep title below nav plus extra margin which serves as padding to nav
            return this.additionalTopMarginContent({ prefix: 'top', sm: 0, md: 0, lg: 0, xl: 12 });
        },
        stickyClassName(): string {
            return this.enabled && this.stickyEnabled ? `${this.stickyClass} ${this.topMarginContent.class}` : '';
        },
        stickyStyle(): Record<string, string> {
            return this.enabled && this.stickyEnabled ? this.topMarginContent.style : {};
        },
    },
    mounted() {
        this.root = this.$refs.root as HTMLElement;
        this.updateStickyState();
        // Debounce viewport-driven recalculation; host ResizeObserver caused self-trigger loops on sticky changes.
        this.resizeHandler = debounce(() => this.updateStickyState(), 300);
        window.addEventListener('resize', this.resizeHandler, { passive: true });
    },
    beforeUnmount() {
        if (this.resizeHandler) {
            window.removeEventListener('resize', this.resizeHandler);
            this.resizeHandler.cancel();
        }
    },
    methods: {
        getHostElement(): HTMLElement {
            return (this.root?.closest('section') as HTMLElement | null) || this.root!;
        },
        updateStickyState() {
            const host = this.getHostElement();
            if (!host) return;
            this.stickyEnabled = host.getBoundingClientRect().height > window.innerHeight * this.minHeightRatio;
        },
    },
};
</script>
